import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { PlannerV2AreasGetWithPendingChangesDocument } from "../queries.generated";

export default function usePlannerV2AreasGetWithPendingChanges({ onDate }: { onDate: string }) {
    const {
        data: latestData,
        previousData,
        loading,
        error,
    } = useQuery(PlannerV2AreasGetWithPendingChangesDocument, {
        variables: { onDate },
        fetchPolicy: "cache-first",
    });

    const data = latestData ?? previousData;

    const areasToBeCancelled = useMemo(
        () => data?.plannerV2AreasGetWithPendingChanges.toBeCancelled ?? [],
        [data?.plannerV2AreasGetWithPendingChanges.toBeCancelled],
    );
    const areasToBePlanned = useMemo(
        () => data?.plannerV2AreasGetWithPendingChanges.toBePlanned ?? [],
        [data?.plannerV2AreasGetWithPendingChanges.toBePlanned],
    );

    const numberOfVisitsToBeCancelled = useMemo(() => {
        return areasToBeCancelled.reduce((sum, area) => sum + area.plannerVisits.length, 0);
    }, [areasToBeCancelled]);

    const numberOfVisitsToBePlanned = useMemo(() => {
        return areasToBePlanned.reduce((sum, area) => sum + area.plannerVisits.length, 0);
    }, [areasToBePlanned]);

    const allPendingAreas = useMemo(() => {
        return [...areasToBeCancelled, ...areasToBePlanned].map(area => area.plannerArea);
    }, [areasToBeCancelled, areasToBePlanned]);

    const allPendingVisitIds = useMemo(() => {
        return [...areasToBeCancelled, ...areasToBePlanned].flatMap(area => area.plannerVisits.map(visit => visit.id));
    }, [areasToBeCancelled, areasToBePlanned]);

    return {
        loading,
        error,
        data: {
            areasToBeCancelled,
            areasToBePlanned,
            allPendingAreas,
            allPendingVisitIds,
            numberOfVisitsToBeCancelled,
            numberOfVisitsToBePlanned,
            totalDrafts: numberOfVisitsToBeCancelled + numberOfVisitsToBePlanned,
        },
    };
}
